import { ArrowLeft, Mail, UserPlus } from '@tamagui/lucide-icons';
import { ShowPasswordInputIcon } from '@unistocks/components/basic/input';
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { AuthFormProps } from '@unistocks/features/authentication/auth-form-props';
import { t } from '@unistocks/localization';
import { usePasswordValidationSchema } from '@unistocks/shared/utils/password-validator';
import React, { useMemo, useState } from 'react';
import { z } from 'zod';

export type RegisterFormProps = AuthFormProps<RegisterFormData>;

export type RegisterFormData = {
  username: string;
  password: {
    password: string;
    confirmPassword: string;
  };
};

export const RegisterForm: React.FC<RegisterFormProps> = ({
  isSubmitting,
  onSubmit,
  toggle
}: RegisterFormProps) => {
  const { data: passwordSchema } = usePasswordValidationSchema({ withConfirmField: true });

  const schema = useMemo(
    () =>
      passwordSchema &&
      z.object({
        username: requiredSchema(
          z.email(t('::ThisFieldIsNotAValidEmailAddress.')),
          undefined,
          t('::The {0} field is required.', { 0: t('AbpAccount::EmailAddress') })
        ),
        password: passwordSchema
      }),
    [passwordSchema]
  );

  const [showPassword, setShowPassword] = useState(false);

  return (
    <Form
      schema={schema}
      isLoading={isSubmitting}
      onSubmit={onSubmit}
      customTrigger={
        <SubmitButtons
          cancelButtonIcon={ArrowLeft}
          cancelButtonText={t('::Back')}
          onCancel={toggle}
          submitButtonIcon={UserPlus}
          submitButtonText={t('AbpAccount::Register')}
        />
      }
    >
      <Form.Input
        name={'username'}
        label={t('AbpAccount::EmailAddress')}
        keyboardType={'email-address'}
        textContentType={'username'}
        autoCapitalize={'none'}
        isRequired
        iconAfter={<Mail />}
      />

      <Form.Input
        name={'password.password'}
        label={t('AbpAccount::Password')}
        type={showPassword ? 'text' : 'password'}
        textContentType={'password'}
        autoCapitalize={'none'}
        isRequired
        iconAfter={
          <ShowPasswordInputIcon showPassword={showPassword} setShowPassword={setShowPassword} />
        }
      />

      <Form.Input
        name={'password.confirmPassword'}
        label={t('AbpAccount::ConfirmPassword')}
        type={showPassword ? 'text' : 'password'}
        textContentType={'password'}
        autoCapitalize={'none'}
        returnKeyType={'send'}
        isRequired
        iconAfter={
          <ShowPasswordInputIcon showPassword={showPassword} setShowPassword={setShowPassword} />
        }
      />
    </Form>
  );
};

RegisterForm.displayName = 'RegisterForm';
